/**
 *
 *  PartnerProductBadge -- used in product lists
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 28/02/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback } from "react";
import PropTypes from "prop-types";

// Icons
import WoBoxingIcon from "../../icons/WoBoxingIcon";
import WoKickboxingIcon from "../../icons/WoKickboxingIcon";
import WoYogaIcon from "../../icons/WoYogaIcon";
import WoPilatesIcon from "../../icons/WoPilatesIcon";
import WoBalletFitIcon from "../../icons/WoBalletFitIcon";
import WoPersonalTrainingIcon from "../../icons/WoPersonalTrainingIcon";
import WoPrePostNatalIcon from "../../icons/WoPrePostNatalIcon";
import WoMeditationIcon from "../../icons/WoMeditationIcon";
import WoStretchingIcon from "../../icons/WoStretchingIcon";
import WoRunningIcon from "../../icons/WoRunningIcon";
import WoLifestyleCoachingIcon from "../../icons/WoLifestyleCoachingIcon";
import WoMotivationIcon from "../../icons/WoMotivationIcon";
import WoSoundBathsIcon from "../../icons/WoSoundBathsIcon";
import WoStressManagementIcon from "../../icons/WoStressManagementIcon";
import WoCymbiotikaCertifiedIcon from "../../icons/WoCymbiotikaCertifiedIcon";
import WoSpiritualGangsterIcon from "../../icons/WoSpiritualGangsterIcon";

import { makeUrlString } from "../../utils";

const workoutIconsMap = {
  boxing: WoBoxingIcon,
  kickboxing: WoKickboxingIcon,
  yoga: WoYogaIcon,
  pilates: WoPilatesIcon,
  ballet /*balletFit*/: WoBalletFitIcon,
  "personal-training" /*personalTraining*/: WoPersonalTrainingIcon,
  "prepost-natal" /*prePostNatal*/: WoPrePostNatalIcon,
  meditation: WoMeditationIcon,
  stretching: WoStretchingIcon,
  running: WoRunningIcon,
  "lifestyle-coaching": WoLifestyleCoachingIcon,
  "motivational-coaching": WoMotivationIcon,
  "sound-baths": WoSoundBathsIcon,
  "sound-healing": WoSoundBathsIcon,
  "stress-management": WoStressManagementIcon,
  "cymbiotika-certified": WoCymbiotikaCertifiedIcon,
  "spiritual-gangster-certified": WoSpiritualGangsterIcon,
};

export const WorkoutIcon = ({ className, label, size }) => {
  let WorkoutIcon;

  // Correct! JSX type can be a capitalized variable.
  if (label) {
    WorkoutIcon = workoutIconsMap[makeUrlString(label)];
  }

  WorkoutIcon = WorkoutIcon || WoPersonalTrainingIcon;

  return <WorkoutIcon className={className} size={size} />;
};

export const PartnerProductBadge = ({
  className,
  iconClassName,
  labelClassName,
  active,
  element: Element = "div",
  showIcon = true,
  border = true,
  product,
  onClick,
}) => {
  const handleClick = useCallback(() => {
    onClick && onClick(product);
  }, [product, onClick]);

  const productName = product && product.name;

  return (
    <Element
      className={`${
        className ? className + " " : "tbk-h-4 tbk-py-0.5 tbk-px-1"
      }tb-badge-partner-product tbk-flex tbk-items-center tbk-whitespace-nowrap tbk-rounded-lg ${
        border ? "tbk-border" : ""
      } tbk-border-solid ${
        active
          ? "tbk-border-main-grey tbk-bg-grey-main tbk-text-basic-white"
          : "_hover:tbk-bg-blue-grey-light tbk-border-blue-grey-light tbk-bg-basic-white tbk-text-grey-main hover:tbk-border-grey-main hover:tbk-text-grey-main"
      }`}
      onClick={handleClick}
    >
      {showIcon ? (
        <WorkoutIcon className={iconClassName} label={productName} />
      ) : null}
      <span
        className={`${
          labelClassName
            ? `${labelClassName} `
            : "tbk-text-small-bold tbk-uppercase "
        }tbk-overflow-hidden tbk-text-ellipsis`}
      >
        {productName}
      </span>
    </Element>
  );
};

PartnerProductBadge.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    type: PropTypes.string,
    region: PropTypes.string,
    headline: PropTypes.string,
    category: PropTypes.string,
    descriptionTitle: PropTypes.string,
    description: PropTypes.string,
    sessionTitle: PropTypes.string,
    skillTitle: PropTypes.string,
    durations: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        minutes: PropTypes.number,
      })
    ),
    skills: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      })
    ),
  }),
};

export default PartnerProductBadge;
